export const STEP_TYPES = {
  COMMAND: 'command',
  WAIT: 'wait',
  MEASURE: 'measure',
  GET_RESULT_AND_PIC: 'get_result_and_pic'
} as const;

export type StepType = (typeof STEP_TYPES)[keyof typeof STEP_TYPES];

export const FAILURE_MODES = {
  ABORT: 'abort',
  PAUSE: 'pause',
  CONTINUE: 'continue'
} as const;

export type FailureMode = (typeof FAILURE_MODES)[keyof typeof FAILURE_MODES];

export type MqttQos = 0 | 1 | 2;

// --- Simplified step types (flat JSON, no boilerplate) ---

export type CommandStep = {
  type?: 'command';
  action: string;
  description?: string;
  topic?: string;
  payload?: string;
  timeoutMs?: number;
  qos?: MqttQos;
  maxRetries?: number;
  onFailure?: FailureMode;
};

export type WaitStep = {
  type?: 'wait';
  durationMs: number;
  label?: string;
  maxRetries?: number;
  onFailure?: FailureMode;
};

export type ResultMapping = {
  targetFunctionId: string;
  targetTestPointId: string;
  targetField: string;
  sourcePayloadPath: string;
};

export type MeasureStep = {
  type: 'measure' | 'get_result_and_pic';
  action?: string;
  description?: string;
  topic?: string;
  payload?: string;
  targetField?: 'asFound' | 'asLeft';
  testPointPath?: string;
  resultMapping?: ResultMapping;
  timeoutMs?: number;
  qos?: MqttQos;
  maxRetries?: number;
  onFailure?: FailureMode;
};

export type ScriptStep = CommandStep | WaitStep | MeasureStep;

export type ScriptStepWithId = ScriptStep & {
  id: string;
  index: number;
};

/**
 * Infers the step type from the JSON shape.
 * - Explicit `type` of 'measure' or 'get_result_and_pic' → measure
 * - Has `durationMs` → wait
 * - Has `action` (or legacy `command`) → command
 */
export const inferStepType = (step: Record<string, unknown>): StepType => {
  const explicit = step.type as string | undefined;
  if (
    explicit === 'measure' ||
    explicit === 'get_result_and_pic' ||
    explicit === 'record_measurement'
  )
    return explicit === 'record_measurement'
      ? 'measure'
      : (explicit as StepType);
  if ('durationMs' in step) return 'wait';
  if ('action' in step || 'command' in step) return 'command';
  // Fall back to explicit type if present (legacy)
  if (explicit && Object.values(STEP_TYPES).includes(explicit as StepType))
    return explicit as StepType;
  return 'command';
};

/**
 * Hydrates a raw JSON step into a typed ScriptStep.
 * Handles both legacy (nested command.action) and new (flat action) formats.
 */
export const hydrateStep = (raw: Record<string, unknown>): ScriptStep => {
  const type = inferStepType(raw);

  // Flatten legacy command.action → action
  let action = raw.action as string | undefined;
  if (!action && raw.command && typeof raw.command === 'object') {
    action = (raw.command as { action?: string }).action;
  }

  if (type === 'wait') {
    return {
      type: 'wait',
      durationMs: (raw.durationMs as number) ?? 1000,
      label: raw.label as string | undefined
    } satisfies WaitStep;
  }

  if (type === 'measure' || type === 'get_result_and_pic') {
    return {
      type,
      action,
      description: raw.description as string | undefined,
      topic: raw.topic as string | undefined,
      payload: raw.payload as string | undefined,
      targetField: (raw.targetField as 'asFound' | 'asLeft') ?? 'asFound',
      testPointPath: raw.testPointPath as string | undefined,
      resultMapping: raw.resultMapping as ResultMapping | undefined,
      ...(raw.timeoutMs != null && { timeoutMs: raw.timeoutMs as number })
    } satisfies MeasureStep;
  }

  // command
  return {
    action: action ?? '',
    description: raw.description as string | undefined,
    topic: raw.topic as string | undefined,
    payload: raw.payload as string | undefined,
    ...(raw.timeoutMs != null && { timeoutMs: raw.timeoutMs as number })
  } satisfies CommandStep;
};

/**
 * Strips runtime/default fields from a step for clean JSON persistence.
 * Removes: type (for command/wait), qos, maxRetries, onFailure when they match defaults.
 * Removes legacy nested `command` wrapper.
 */
export const stripStepDefaults = (
  step: ScriptStep
): Record<string, unknown> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = { ...step } as Record<string, any>;

  // Remove type for command and wait (inferred from shape)
  const type = inferStepType(raw);
  if (type === 'command' || type === 'wait') {
    delete raw.type;
  }

  // Remove default values
  if (raw.qos === 1 || raw.qos == null) delete raw.qos;
  if (raw.maxRetries === 0 || raw.maxRetries == null) delete raw.maxRetries;
  if (raw.onFailure === 'abort' || raw.onFailure == null) delete raw.onFailure;

  // Remove legacy nested command wrapper (should already be flat, but just in case)
  delete raw.command;

  // Remove empty optional fields
  if (!raw.topic) delete raw.topic;
  if (!raw.payload) delete raw.payload;
  if (!raw.label) delete raw.label;
  if (!raw.testPointPath) delete raw.testPointPath;
  if (!raw.description) delete raw.description;

  return raw;
};

export type TestPointRef = {
  functionId: string;
  nominal: number;
  unit: string;
  mode: string;
  label: string;
  frequency?: number;
  /** Absolute per-point tolerance override in the point's unit. Replaces the catalog value. */
  toleranceOverride?: number;
};

export type ScriptData = {
  steps?: ScriptStep[];
  setupSteps?: ScriptStep[];
  teardownSteps?: ScriptStep[];
  items?: Array<{ globalId: string; scriptId: string }>;
};

export type ItemScriptData = {
  testPointRef: TestPointRef;
  beforeSteps: ScriptStep[];
  calibrationSteps: ScriptStep[];
  afterSteps: ScriptStep[];
};

export type ItemRef = { globalId: string; scriptId: string };

/**
 * Master script data — the three-part shape: a pickup phase, an ordered list of
 * test-point item references, and a return phase. Replaces the legacy
 * setupSteps/teardownSteps/cells/executionOrder rack shape.
 */
export type MasterScriptData = {
  pickupSteps: ScriptStep[];
  items: ItemRef[];
  returnSteps: ScriptStep[];
};

// --- Legacy rack helpers (back-compat only) ---
// Retained so hydrateMasterScriptData can map an old cells[] master into the new
// three-part shape on load. Nothing authors rack data anymore.

export type ExecutionOrder = 'row-first' | 'column-first';

export type RackCellEntry = {
  position: string; // "A01", "B03", "D05" — Excel-style, col letter + zero-padded row
  items: ItemRef[];
};

export const RACK_COLUMNS = ['A', 'B', 'C', 'D'] as const;
export const RACK_ROWS = ['01', '02', '03', '04', '05'] as const;

export const allRackPositions = (order: ExecutionOrder): string[] => {
  if (order === 'column-first') {
    return RACK_COLUMNS.flatMap(col => RACK_ROWS.map(row => `${col}${row}`));
  }
  return RACK_ROWS.flatMap(row => RACK_COLUMNS.map(col => `${col}${row}`));
};

/**
 * Hydrates a parsed master script (new OR legacy shape) into the three-part
 * {pickupSteps, items, returnSteps} shape for display in the Builder.
 *
 * Legacy mapping (lossless for display — the semantic split is the migration's job):
 *   setupSteps → pickupSteps
 *   cells[].items[] walked in executionOrder (canonical positions first, then any
 *     off-grid cells in array order) → ordered items[]
 *   teardownSteps → returnSteps
 */
export const hydrateMasterScriptData = (
  parsed: Record<string, unknown>
): MasterScriptData => {
  const pickupSteps = Array.isArray(parsed.pickupSteps)
    ? (parsed.pickupSteps as ScriptStep[])
    : Array.isArray(parsed.setupSteps)
      ? (parsed.setupSteps as ScriptStep[])
      : [];

  const returnSteps = Array.isArray(parsed.returnSteps)
    ? (parsed.returnSteps as ScriptStep[])
    : Array.isArray(parsed.teardownSteps)
      ? (parsed.teardownSteps as ScriptStep[])
      : [];

  let items: ItemRef[] = [];
  if (Array.isArray(parsed.items)) {
    items = parsed.items as ItemRef[];
  } else if (Array.isArray(parsed.cells)) {
    const order = (parsed.executionOrder as ExecutionOrder) ?? 'row-first';
    const cells = parsed.cells as RackCellEntry[];
    const canonical = allRackPositions(order);
    const inGrid = canonical.flatMap(
      pos => cells.find(c => c.position === pos)?.items ?? []
    );
    const offGrid = cells
      .filter(c => !canonical.includes(c.position))
      .flatMap(c => c.items);
    items = [...inGrid, ...offGrid];
  }

  return { pickupSteps, items, returnSteps };
};

export const STEP_TYPE_LABELS: Record<StepType, string> = {
  command: 'Command',
  wait: 'Wait',
  measure: 'Measure',
  get_result_and_pic: 'Get Result and Pic'
};
